/**
 * Shared DB-backed preview-pair persistence.
 *
 * The 2-stage flows (Baseball Trend, Talking Avatar) generate a small
 * "preview pair" the user picks from before the expensive Stage-2 render.
 * The pair is server state: `generateXService.ts` writes `generated_images`
 * rows via `insertPendingImages` (shared `batch_id`, per-flow `pipeline`,
 * status pending→completed), realtime-synced into `GeneratedImagesContext`.
 *
 * Driving the UI off that store (NOT ephemeral local `useState`) is what
 * makes a refresh / redirect / auth wall / top-up mid-generation resume —
 * exactly how Profile Photo behaves. This hook is the single mechanism;
 * adding a future preview-pair flow is one call + spreading `persisted`
 * into that flow's `useFlowPersistence`.
 *
 * The flow still owns: its own `useFlowPersistence` (upload/preset/etc. plus
 * the spread `persisted`), scroll-to-anchor UX, and the generate request.
 * This hook owns: batch derivation, the dismiss high-water mark, the
 * optimistic bridge, and selection-by-stable-id.
 */

import { useCallback, useMemo, useState } from "react";
import {
  useGeneratedImages,
  type GeneratedImage,
} from "@/hooks/useGeneratedImages";

interface UseDbBackedPreviewPairOptions {
  /** Flow pipeline tag — MUST equal what the server writes to
   *  `generated_images.pipeline` (e.g. "baseball_trend"). */
  pipeline: string;
}

/** The subset persisted across the auth/credit wall by the flow's own
 *  `useFlowPersistence`. The preview pair itself is NOT persisted — it's
 *  server state rebuilt from the realtime store on every mount. */
export interface PreviewPairPersistedState {
  selectedPreviewImageId: string | null;
  selectedHistoricalUrl: string | null;
  dismissedPreviewBeforeTs: string | null;
}

export function useDbBackedPreviewPair({
  pipeline,
}: UseDbBackedPreviewPairOptions) {
  /** Stable id (`generated_images.id`) of the preview picked from the CURRENT
   *  batch. Persisted; on restore resolved against the live batch. */
  const [selectedPreviewImageId, setSelectedPreviewImageId] = useState<
    string | null
  >(null);
  /** URL of a preview picked from history (NOT the current pair). Mutually
   *  exclusive with `selectedPreviewImageId`. Drives the render when set. */
  const [selectedHistoricalUrl, setSelectedHistoricalUrl] = useState<
    string | null
  >(null);
  /** Sub-second bridge between the generate click and the first pending row
   *  landing in the realtime store; the batch is the durable truth. */
  const [optimisticPreviewing, setOptimisticPreviewing] = useState(false);
  /** Server-timestamp high-water mark: hide every batch whose newest slot's
   *  `created_at` is ≤ this. Set by `clearPreviews()`; a later generation
   *  writes a strictly-newer server `created_at` and reappears. Compared
   *  server-ts vs server-ts only — never against client `Date.now()`. */
  const [dismissedPreviewBeforeTs, setDismissedPreviewBeforeTs] = useState<
    string | null
  >(null);

  // `pendingImages` (not local state) is what makes mid-generation refresh
  // resume. `images` (completed) powers the "previous previews" picker.
  const { images: pastPreviewImages, pendingImages: dbPendingPreviewImages } =
    useGeneratedImages(undefined, pipeline);

  /** Every flow image (in-flight ∪ completed). Shared by the batch picker
   *  and `clearPreviews` so the dismiss mark uses the same set the grid
   *  derives from. */
  const allPreviewImages = useMemo(
    () => [...dbPendingPreviewImages, ...pastPreviewImages],
    [dbPendingPreviewImages, pastPreviewImages],
  );

  /** Most-recent batch (pending ∪ completed), grouped by server `batch_id`,
   *  excluding batches dismissed by a config change. */
  const currentPreviewBatch = useMemo<GeneratedImage[]>(() => {
    if (allPreviewImages.length === 0) return [];
    const byBatch = new Map<string, GeneratedImage[]>();
    for (const img of allPreviewImages) {
      const key = img.batch_id ?? img.id;
      const group = byBatch.get(key);
      if (group) group.push(img);
      else byBatch.set(key, [img]);
    }
    const dismissedT = dismissedPreviewBeforeTs
      ? new Date(dismissedPreviewBeforeTs).getTime()
      : -1;
    let best: GeneratedImage[] = [];
    let bestT = -1;
    for (const group of byBatch.values()) {
      const t = Math.max(...group.map((g) => new Date(g.created_at).getTime()));
      if (t <= dismissedT) continue;
      if (t > bestT) {
        bestT = t;
        best = group;
      }
    }
    return [...best].sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime() ||
        a.id.localeCompare(b.id),
    );
  }, [allPreviewImages, dismissedPreviewBeforeTs]);

  /** Completed slots of the current batch, stable order. */
  const currentBatchCompleted = useMemo(
    () =>
      currentPreviewBatch.filter(
        (img) => img.status === "completed" && img.image_url,
      ),
    [currentPreviewBatch],
  );

  /** Completed preview URLs of the current pair. */
  const previewUrls = useMemo(
    () => currentBatchCompleted.map((img) => img.image_url as string),
    [currentBatchCompleted],
  );

  const batchInFlight = useMemo(
    () =>
      currentPreviewBatch.some(
        (img) => img.status === "pending" || img.status === "processing",
      ),
    [currentPreviewBatch],
  );
  const isPreviewing = optimisticPreviewing || batchInFlight;

  /** Progress-bar anchor — earliest in-flight slot's `created_at`. */
  const previewStartedAt = useMemo(() => {
    if (!batchInFlight) return null;
    const times = currentPreviewBatch
      .filter((i) => i.status === "pending" || i.status === "processing")
      .map((i) => i.created_at)
      .sort();
    return times[0] ?? null;
  }, [batchInFlight, currentPreviewBatch]);

  /** Index into `previewUrls` of the picked preview, resolved from the
   *  persisted stable id against the live batch (null if not present). */
  const selectedPreviewIndex = useMemo(() => {
    if (!selectedPreviewImageId) return null;
    const i = currentBatchCompleted.findIndex(
      (img) => img.id === selectedPreviewImageId,
    );
    return i >= 0 ? i : null;
  }, [selectedPreviewImageId, currentBatchCompleted]);

  /** URL the Stage-2 render should use: a history pick wins, else the
   *  picked current-batch slot. */
  const effectivePreviewUrl = useMemo(() => {
    if (selectedHistoricalUrl) return selectedHistoricalUrl;
    if (!selectedPreviewImageId) return null;
    return (
      currentBatchCompleted.find((img) => img.id === selectedPreviewImageId)
        ?.image_url ?? null
    );
  }, [selectedHistoricalUrl, selectedPreviewImageId, currentBatchCompleted]);

  const hasPreviewSelection = effectivePreviewUrl !== null;

  /** Pick a slot from the current pair by grid index. Persist by stable id
   *  (survives refresh), not array index. No scroll — the flow adds its own. */
  const selectByIndex = useCallback(
    (idx: number) => {
      const picked = currentBatchCompleted[idx];
      if (!picked) return;
      setSelectedPreviewImageId(picked.id);
      setSelectedHistoricalUrl(null);
    },
    [currentBatchCompleted],
  );

  /** Pick a preview from history (mutually exclusive with the current pair). */
  const selectHistorical = useCallback((url: string) => {
    setSelectedHistoricalUrl(url);
    setSelectedPreviewImageId(null);
  }, []);

  /** Invalidate the displayed pair (config changed). We can't delete the DB
   *  rows, so drop a high-water mark at the newest existing slot; the next
   *  generation writes a strictly-newer server `created_at` and reappears. */
  const clearPreviews = useCallback(() => {
    let newest: string | null = null;
    for (const img of allPreviewImages) {
      if (!newest || new Date(img.created_at) > new Date(newest)) {
        newest = img.created_at;
      }
    }
    if (newest) setDismissedPreviewBeforeTs(newest);
    setSelectedPreviewImageId(null);
    setSelectedHistoricalUrl(null);
    setOptimisticPreviewing(false);
  }, [allPreviewImages]);

  /** Call right before kicking off a preview request — instant feedback +
   *  drop any prior pick (a new pair supersedes it). */
  const beginPreview = useCallback(() => {
    setOptimisticPreviewing(true);
    setSelectedPreviewImageId(null);
    setSelectedHistoricalUrl(null);
  }, []);

  /** Call in the request's `finally` — the endpoint is synchronous through
   *  completion, so by here the batch is terminal in the DB and takes over. */
  const endPreview = useCallback(() => setOptimisticPreviewing(false), []);

  const persisted: PreviewPairPersistedState = {
    selectedPreviewImageId,
    selectedHistoricalUrl,
    dismissedPreviewBeforeTs,
  };

  /** Spread inside the flow's `useFlowPersistence` `onRestore`. */
  const restorePersisted = useCallback(
    (s: Record<string, unknown>) => {
      if (typeof s.selectedPreviewImageId === "string") {
        setSelectedPreviewImageId(s.selectedPreviewImageId);
      }
      if (typeof s.selectedHistoricalUrl === "string") {
        setSelectedHistoricalUrl(s.selectedHistoricalUrl);
      }
      if (typeof s.dismissedPreviewBeforeTs === "string") {
        setDismissedPreviewBeforeTs(s.dismissedPreviewBeforeTs);
      }
    },
    [],
  );

  return {
    // Display (same names the flow grids already consume)
    previewUrls,
    selectedPreviewIndex,
    selectedHistoricalUrl,
    isPreviewing,
    previewStartedAt,
    pastPreviewImages,
    // Render input
    effectivePreviewUrl,
    hasPreviewSelection,
    // Actions (pure; flow adds scroll/UX)
    selectByIndex,
    selectHistorical,
    clearPreviews,
    beginPreview,
    endPreview,
    // Persistence bridge for the flow's own useFlowPersistence
    persisted,
    restorePersisted,
  };
}
